// Pure calculation helpers for Business Zakat
// No React, no side effects.

import { businessTypes, zakatFitrRates } from '../config/businessZakatConfig';
import { nisabConstants } from '../config/zakatConfig';

/**
 * Locate the business config object by its id.
 */
export const findBusinessConfig = (businessId) => {
  if (!businessId) return null;
  return (
    Object.values(businessTypes)
      .flatMap((cat) => cat.types)
      .find((t) => t.id === businessId) || null
  );
};

/**
 * Livestock Zakat calculation (simplified per traditional rules).
 */
export const computeLivestockZakat = (assetValues) => {
  const camels = parseInt(assetValues.camels, 10) || 0;
  const cattle = parseInt(assetValues.cattle, 10) || 0;
  const sheep = parseInt(assetValues.sheep, 10) || 0;
  const goats = parseInt(assetValues.goats, 10) || 0;
  const sheepValue = parseFloat(assetValues.sheepValue) || 100;

  let zakatSheep = 0;
  const totalSmall = sheep + goats;

  if (totalSmall >= 40 && totalSmall <= 120) zakatSheep = 1;
  else if (totalSmall <= 200) zakatSheep = 2;
  else if (totalSmall <= 300) zakatSheep = 3;
  else if (totalSmall > 300) zakatSheep = 3 + Math.floor((totalSmall - 300) / 100);

  if (camels >= 5) zakatSheep += Math.ceil(camels / 5);
  const cattleCalves = cattle >= 30 ? Math.floor(cattle / 30) : 0;

  return (zakatSheep + cattleCalves) * sheepValue;
};

/**
 * Compute Zakat al-Fitr per family size for the active country.
 */
export const computeZakatFitr = (countryCode, familyMembers) => {
  const countryRate = zakatFitrRates[countryCode] || zakatFitrRates.US;
  return {
    perPerson: countryRate.rate,
    totalMembers: familyMembers,
    totalAmount: countryRate.rate * familyMembers,
    currency: countryRate.currency,
  };
};

/**
 * Compute the final Business Zakat result.
 */
export const computeBusinessZakat = ({
  businessId,
  assetValues,
  deductionValues,
  nisabStandard,
  goldPricePerGram,
  silverPricePerGram,
  showDualNisab,
  instalmentMonths,
  currency,
  currencySymbol,
}) => {
  const businessConfig = findBusinessConfig(businessId);
  if (!businessConfig) return null;

  const totalAssets = Object.values(assetValues).reduce(
    (sum, v) => sum + (parseFloat(v) || 0),
    0
  );
  const totalDeductions = Object.values(deductionValues).reduce(
    (sum, v) => sum + (parseFloat(v) || 0),
    0
  );
  const netWorth = totalAssets - totalDeductions;

  const goldNisab = nisabConstants.goldGrams * goldPricePerGram;
  const silverNisab = nisabConstants.silverGrams * silverPricePerGram;
  const effectiveNisab = nisabStandard === 'gold' ? goldNisab : silverNisab;
  const isEligible = netWorth >= effectiveNisab;

  let zakatRate = 0.025;
  let zakatAmount = 0;

  if (businessConfig.method === 'harvest') {
    const waterRate = parseFloat(assetValues.waterSource) || 0.1;
    zakatRate = waterRate;
    zakatAmount = isEligible ? netWorth * zakatRate : 0;
  } else if (businessConfig.method === 'perhead') {
    zakatAmount = computeLivestockZakat(assetValues);
  } else {
    zakatAmount = isEligible ? netWorth * zakatRate : 0;
  }

  const dualResult = showDualNisab
    ? {
        goldNisab: {
          threshold: goldNisab,
          isEligible: netWorth >= goldNisab,
          zakatAmount: netWorth >= goldNisab ? netWorth * 0.025 : 0,
        },
        silverNisab: {
          threshold: silverNisab,
          isEligible: netWorth >= silverNisab,
          zakatAmount: netWorth >= silverNisab ? netWorth * 0.025 : 0,
        },
      }
    : null;

  const monthlyInstalment = zakatAmount / instalmentMonths;

  return {
    businessType: businessConfig,
    totalAssets,
    totalDeductions,
    netWorth,
    nisab: effectiveNisab,
    nisabStandard,
    goldNisab,
    silverNisab,
    isEligible,
    zakatRate: zakatRate * 100,
    zakatAmount,
    dualResult,
    instalments: {
      months: instalmentMonths,
      monthlyAmount: monthlyInstalment,
    },
    calculatedAt: new Date(),
    currency,
    currencySymbol,
  };
};
